import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as accessApi from './api'
import type { CreateRuleRequest, UpdateRuleRequest } from './types'

export const accessKeys = {
  fields: (objectType: string) => ['access', 'fields', objectType] as const,
  rules:  (roleCode: string, objectType: string) =>
    ['access', 'rules', 'role', roleCode, 'object', objectType] as const,
  teamRules: (teamId: number, objectType: string) =>
    ['access', 'rules', 'team', teamId, 'object', objectType] as const,
}

export function useFields(objectType: string) {
  return useQuery({
    queryKey: accessKeys.fields(objectType),
    queryFn: () => accessApi.getFields(objectType),
  })
}

export function useRules(roleCode: string, objectType: string) {
  return useQuery({
    queryKey: accessKeys.rules(roleCode, objectType),
    queryFn: () => accessApi.listRules(roleCode, objectType),
    enabled: !!roleCode,
  })
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, roleCode: string, objectType: string) {
  qc.invalidateQueries({ queryKey: accessKeys.rules(roleCode, objectType) })
  qc.invalidateQueries({ queryKey: ['contracts'] })   // visibility may change list
}

export function useCreateRule(roleCode: string, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateRuleRequest) => accessApi.createRule(roleCode, objectType, payload),
    onSuccess: () => invalidateRules(qc, roleCode, objectType),
  })
}

export function useUpdateRule(roleCode: string, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { ruleId: number; payload: UpdateRuleRequest }) =>
      accessApi.updateRule(roleCode, objectType, vars.ruleId, vars.payload),
    onSuccess: () => invalidateRules(qc, roleCode, objectType),
  })
}

export function useDeleteRule(roleCode: string, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) => accessApi.deleteRule(roleCode, objectType, ruleId),
    onSuccess: () => invalidateRules(qc, roleCode, objectType),
  })
}

// Team visibility rules - parallel hooks
export function useTeamRules(teamId: number, objectType: string) {
  return useQuery({
    queryKey: accessKeys.teamRules(teamId, objectType),
    queryFn: () => accessApi.listTeamRules(teamId, objectType),
    enabled: teamId > 0,
  })
}

function invalidateTeamRules(qc: ReturnType<typeof useQueryClient>, teamId: number, objectType: string) {
  qc.invalidateQueries({ queryKey: accessKeys.teamRules(teamId, objectType) })
  qc.invalidateQueries({ queryKey: ['contracts'] })
}

export function useCreateTeamRule(teamId: number, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateRuleRequest) => accessApi.createTeamRule(teamId, objectType, payload),
    onSuccess: () => invalidateTeamRules(qc, teamId, objectType),
  })
}

export function useUpdateTeamRule(teamId: number, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { ruleId: number; payload: UpdateRuleRequest }) =>
      accessApi.updateTeamRule(teamId, objectType, vars.ruleId, vars.payload),
    onSuccess: () => invalidateTeamRules(qc, teamId, objectType),
  })
}

export function useDeleteTeamRule(teamId: number, objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) => accessApi.deleteTeamRule(teamId, objectType, ruleId),
    onSuccess: () => invalidateTeamRules(qc, teamId, objectType),
  })
}
